import React, { createContext, useContext } from 'react';
import type { OsobyPodnikatelRozsireneDto } from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useProcessControl } from '@gov-nx/core/hooks';
import { getRequiredDataBoxes } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { hasProperty, hasSomeProperties, propEq } from '@gov-nx/core/types';
import { useDataBoxAccess } from '@gov-nx/module/data-box';
import { ServiceCode } from '@gov-nx/module/service';
import { useBoolean } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import { RosComplaintVariant } from '../reklamace/options';
import { useRosExtendedQuery } from '../useRosExtendedQuery';
import type {
	ProfileROSDetailProcessControl,
	ROSDetailContextType,
} from './context.types';
import { getName } from './utils';

const ProfilROSDetailContext =
	createContext<Nullable<ROSDetailContextType>>(null);

interface SettingsROSContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
	ico: number;
	agendaCode?: string;
	onComplaint?: (
		data: OsobyPodnikatelRozsireneDto,
		variant: RosComplaintVariant
	) => void;
	onService: (code: ServiceCode) => void;
}

export function RegistrOsobDetailContextProvider({
	children,
	code,
	ico,
	agendaCode,
	onComplaint,
	onService,
}: SettingsROSContextProviderProps) {
	const { controls, setControls } =
		useProcessControl<ProfileROSDetailProcessControl>({
			complaintModal: null,
			complaintVariant: RosComplaintVariant.AGENDA,
		});
	const isMembersListModalOpen = useBoolean(false);
	const isEstablishmentsListModalOpen = useBoolean(false);
	const isPersonsListModalOpen = useBoolean(false);

	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(
		ServiceCode['vypis-z-zivnostenskeho-rejstriku'],
		application
	);
	const { areAnyConnected } = useDataBoxAccess({
		requiredTypes: requiredDataBoxes,
	});

	const { isLoading, error, getDetailDataByIcoAndAgenda } =
		useRosExtendedQuery();

	const detailData = getDetailDataByIcoAndAgenda(ico, agendaCode);

	return (
		<ProfilROSDetailContext.Provider
			value={{
				code,
				controls: {
					...controls,
					initialLoading: isLoading,
					initialError: error,
				},
				detailData,
				setControls,
				onComplaint,
				onService,

				isMembersListModalOpen,
				isEstablishmentsListModalOpen,
				isPersonsListModalOpen,

				areAnyConnected,

				myInvolvement:
					detailData?.angazovaneOsoby
						?.filter(propEq('mojeAngazovanost', true))
						.filter(hasProperty('role')) ?? [],
				members:
					detailData?.statutarniOrgany?.filter(
						hasSomeProperties([
							'jmeno',
							'prijmeni',
							'ico',
							'nazevSpolecnosti',
							'sidloSpolecnosti',
							'datumNarozeni',
						])
					) ?? [],
				establishments:
					detailData?.provozovny?.filter(
						hasSomeProperties([
							'adresa',
							'dorucovaciAdresa',
							'datumZahajeniCinnosti',
						])
					) ?? [],
				persons:
					detailData?.angazovaneOsoby?.filter(
						hasSomeProperties([
							'jmeno',
							'prijmeni',
							'ico',
							'nazevSpolecnosti',
							'sidloSpolecnosti',
							'datumNarozeni',
						])
					) ?? [],

				name: getName(detailData),
				ico,
				agendaCode,
			}}>
			{children}
		</ProfilROSDetailContext.Provider>
	);
}

export const useProfilROSDetailContext = (): ROSDetailContextType =>
	useContext(ProfilROSDetailContext) as ROSDetailContextType;
